/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Link } from 'react-router-dom';
import PolicyLayout from '../../components/policies/PolicyLayout';

export const PrivacyPolicyPage = () => (
  <PolicyLayout
    title="Privacy Policy"
    effectiveDate="1 February 2026"
    lastUpdated="1 February 2026"
  >
    <p>
      This Privacy Policy explains how <strong>Borrow A Geek Limited</strong> trading as{' '}
      <strong>StudioScheduler</strong> (<strong>"we"</strong>, <strong>"our"</strong>,{' '}
      <strong>"us"</strong>) collects, uses, shares, and protects your personal data when you use
      the StudioScheduler platform (the <strong>"Platform"</strong>).
    </p>
    <p>
      We are committed to your privacy and to processing your personal data in accordance with the
      EU General Data Protection Regulation (<strong>GDPR</strong>), the Irish Data Protection Act
      2018, and the ePrivacy Regulations.
    </p>

    <h2>1. Data controller & contact</h2>
    <p>
      <strong>Data Controller:</strong> Borrow A Geek Limited trading as StudioScheduler,
      18 Henry Street, Kilrush, County Clare V15 CX71, Republic of Ireland.
    </p>
    <p>
      <strong>Privacy contact:</strong>{' '}
      <a href="mailto:privacy@studioscheduler.ie">privacy@studioscheduler.ie</a>
    </p>
    <p>
      <strong>Data Protection Officer (DPO):</strong> Chris Koble —{' '}
      <a href="mailto:dpo@studioscheduler.ie">dpo@studioscheduler.ie</a>
    </p>

    <h2>2. Joint controllership with Studios</h2>
    <p>
      When you use the Platform to interact with a Studio (for example, by making a purchase,
      booking a class, or marking the Studio as a favourite), StudioScheduler and that Studio
      become <strong>joint controllers</strong> of the personal data processed in connection with
      that interaction, within the meaning of Article 26 GDPR.
    </p>
    <p>
      In brief, our respective responsibilities are:
    </p>
    <ul>
      <li>
        <strong>StudioScheduler</strong> is responsible for platform-level obligations: operating
        the technical infrastructure, providing account and authentication services, responding to
        access requests routed through the Platform, security, and cross-Studio functionality
        (e.g., your Client dashboard, marketplace discovery).
      </li>
      <li>
        <strong>Studios</strong> are responsible for the direct client relationship: pricing,
        communications, service delivery, session records, and Studio-specific marketing
        (only when you have consented to receive it or have an existing customer relationship).
      </li>
    </ul>
    <p>
      Regardless of which of us you contact, you may exercise all your GDPR rights against either
      joint controller (see Section 9). We have entered into a joint-controller arrangement with
      each Studio that sets out these responsibilities in detail; the essence of that arrangement is
      summarised in this Privacy Policy and is available on request.
    </p>

    <h2>3. What personal data we collect</h2>
    <h3>Data you provide directly</h3>
    <ul>
      <li><strong>Account:</strong> first name, last name, email address, phone number, password (stored as a bcrypt hash), profile photo (optional), preferred pronouns (optional).</li>
      <li><strong>Booking &amp; purchase history:</strong> classes booked, Class Passes and memberships purchased, cancellations, waitlist activity.</li>
      <li><strong>Communications:</strong> messages you send us or a Studio through the Platform, and your notification preferences.</li>
      <li><strong>Reviews &amp; ratings:</strong> content you post about Studios.</li>
      <li><strong>Support requests:</strong> the contents of tickets you send us and your correspondence.</li>
    </ul>
    <h3>Data collected automatically</h3>
    <ul>
      <li><strong>Session data:</strong> HttpOnly cookies (authentication token, CSRF token) required for security.</li>
      <li><strong>Device &amp; connection:</strong> IP address (truncated after 30 days), user-agent, timezone, and rough geolocation (city-level) if you use the "Studios near you" widget.</li>
      <li><strong>Diagnostic logs:</strong> error reports and payment-SDK errors (which contain a session identifier only — no card data).</li>
    </ul>
    <h3>Data we receive from third parties</h3>
    <ul>
      <li>
        <strong>Payment data</strong> from our payment provider (Ryft Payments):
        card brand and last four digits, cardholder name, billing country, tokenised payment
        method ID, and payment-session status. We <strong>never</strong> store your full card
        number, CVV, or PIN.
      </li>
      <li>
        <strong>Communication delivery status</strong> from SMTP2GO, Brevo, and ClickSend
        (delivered / bounced / opened) for messages we or a Studio send you.
      </li>
    </ul>

    <h2>4. How we use your data — purposes &amp; legal bases</h2>
    <table className="text-xs border rounded-md overflow-hidden my-3">
      <thead className="bg-muted">
        <tr>
          <th className="text-left px-3 py-2">Purpose</th>
          <th className="text-left px-3 py-2">Legal basis (GDPR Art. 6)</th>
        </tr>
      </thead>
      <tbody className="[&_td]:px-3 [&_td]:py-2 [&_td]:border-t">
        <tr><td>Operate your account &amp; provide the Platform</td><td>Contract (6(1)(b))</td></tr>
        <tr><td>Process bookings, Class Pass purchases &amp; payments</td><td>Contract (6(1)(b))</td></tr>
        <tr><td>Send booking / cancellation / refund confirmations</td><td>Contract (6(1)(b))</td></tr>
        <tr><td>Studio-specific marketing (if you opt in or have an existing customer relationship)</td><td>Consent / Legitimate interest (6(1)(a) / (f))</td></tr>
        <tr><td>Platform announcements &amp; product updates</td><td>Legitimate interest (6(1)(f)) — you may opt out at any time</td></tr>
        <tr><td>Fraud prevention, security, and abuse investigation</td><td>Legitimate interest &amp; legal obligation (6(1)(f) / (c))</td></tr>
        <tr><td>Comply with tax, accounting &amp; regulatory obligations</td><td>Legal obligation (6(1)(c))</td></tr>
        <tr><td>Product analytics &amp; service improvement (aggregated, non-identifying)</td><td>Legitimate interest (6(1)(f))</td></tr>
      </tbody>
    </table>

    <h2>5. Sharing your data with Studios</h2>
    <p>
      This is a core commitment of the Platform:
    </p>
    <p>
      <strong>
        We share your personal data with a Studio only if you have an active relationship with that
        Studio — meaning you have (a) purchased a Class Pass, booked a class, or completed a
        transaction with that Studio, or (b) explicitly marked that Studio as a favourite.
      </strong>
    </p>
    <p>
      If you have never transacted with or favourited a Studio, that Studio has <strong>no</strong>{' '}
      visibility of your personal data through the Platform. Studios can see:
    </p>
    <ul>
      <li>Your name, email, and phone number (needed to communicate about your booking);</li>
      <li>Your booking, waitlist, and Class Pass history <em>with that Studio only</em>;</li>
      <li>Your payment status (paid / refunded / failed) <em>for their transactions only</em> — never your card details;</li>
      <li>Any notes they add about you in their own client management system;</li>
      <li>Reviews you have posted about them.</li>
    </ul>
    <p>
      Studios <strong>cannot</strong> see: your bookings or Class Passes with other Studios, your
      full payment method, your favourites of other Studios, or your device / IP data. If you
      unfavourite a Studio and have no active transaction history with them (i.e., no purchase in
      the last 24 months), their access to your data will be revoked.
    </p>

    <h2>6. Other recipients of your data</h2>
    <ul>
      <li>
        <strong>Ryft Payments (payment processor).</strong> Card and transaction data. Ryft is an
        independent controller for its own regulatory obligations. See ryftpay.com/privacy.
      </li>
      <li>
        <strong>SMTP2GO, Brevo, ClickSend</strong> — email &amp; SMS delivery on behalf of us and Studios (processors).
      </li>
      <li>
        <strong>MongoDB Atlas, Google Cloud, Hetzner</strong> — hosting &amp; database (processors, EU-region only).
      </li>
      <li>
        <strong>Sentry</strong> — error monitoring (processor). We scrub PII from error payloads where technically feasible.
      </li>
      <li>
        <strong>OpenStreetMap Nominatim</strong> — receives approximate coordinates only when you use the "Studios near you" feature. Free service, EU-hosted.
      </li>
      <li>
        <strong>Authorities</strong> — where legally required (e.g., a valid court order or Garda request), or to protect life, safety, or rights.
      </li>
      <li>
        <strong>Acquirers</strong> — in the event of a merger, acquisition, or asset sale, your data may be transferred to the acquiring entity under equivalent privacy protections.
      </li>
    </ul>

    <h2>7. International transfers</h2>
    <p>
      Your personal data is stored on servers located in the European Economic Area (EEA). Where a
      processor is outside the EEA (for example, some communication providers), we rely on the
      European Commission's Standard Contractual Clauses or another lawful transfer mechanism to
      ensure your data is protected to an equivalent standard.
    </p>

    <h2>8. How long we keep your data (retention)</h2>
    <ul>
      <li><strong>Account &amp; identity data:</strong> for as long as your account is active, then up to 24 months after last login (or immediately on deletion request, unless a retention obligation applies).</li>
      <li><strong>Bookings &amp; purchase history:</strong> retained for 7 years as required by Irish tax law.</li>
      <li><strong>Payment logs (session IDs, refund records):</strong> 7 years.</li>
      <li><strong>Marketing consent records:</strong> for as long as the consent is valid, plus 3 years after withdrawal.</li>
      <li><strong>Support tickets:</strong> 24 months from resolution.</li>
      <li><strong>Diagnostic logs:</strong> 30 days for full data; aggregated statistics kept longer.</li>
    </ul>

    <h2>9. Your rights under GDPR</h2>
    <p>You have the right to:</p>
    <ul>
      <li><strong>Access</strong> a copy of the personal data we hold about you (Art. 15);</li>
      <li><strong>Rectify</strong> inaccurate or incomplete data (Art. 16);</li>
      <li><strong>Erase</strong> your data ("right to be forgotten"), subject to lawful retention (Art. 17);</li>
      <li><strong>Restrict</strong> processing in certain circumstances (Art. 18);</li>
      <li><strong>Data portability</strong> — receive your data in a structured, machine-readable format (Art. 20);</li>
      <li><strong>Object</strong> to processing based on legitimate interests, including profiling (Art. 21);</li>
      <li><strong>Withdraw consent</strong> at any time where processing is based on consent (Art. 7(3));</li>
      <li><strong>Lodge a complaint</strong> with a supervisory authority — for Ireland, the{' '}
        <a href="https://www.dataprotection.ie" target="_blank" rel="noreferrer">Data Protection Commission</a> (Art. 77).
      </li>
    </ul>
    <p>
      To exercise your rights, email{' '}
      <a href="mailto:privacy@studioscheduler.ie">privacy@studioscheduler.ie</a>. We will respond
      within one month; complex requests may be extended by up to two further months, in which
      case we will notify you.
    </p>

    <h2>10. Automated decision-making &amp; profiling</h2>
    <p>
      We do not make decisions about you based solely on automated processing that produces legal
      or similarly significant effects. Booking, refund, and payment approval decisions involve
      human oversight or are subject to a right of appeal (see the{' '}
      <Link to="/refund-policy">Refund Policy</Link>).
    </p>

    <h2>11. Security</h2>
    <p>
      We implement appropriate technical and organisational measures to protect your data,
      including: HttpOnly authentication cookies, CSRF double-submit tokens, optional MFA for
      Studio owners and administrators, TLS 1.2+ in transit, encryption at rest, role-based access
      controls, and regular security reviews. No system is perfectly secure; we will notify you and
      the Data Protection Commission of any personal data breach that is likely to result in risk
      to your rights, within the 72-hour statutory window.
    </p>

    <h2>12. Children</h2>
    <p>
      The Platform is not directed at children under 16. We do not knowingly collect personal data
      from children under 16 without parental consent. Contact us if you believe a child has
      created an account without authorisation.
    </p>

    <h2>13. Cookies</h2>
    <p>
      Our use of cookies and similar technologies is described in the{' '}
      <Link to="/cookies">Cookie Policy</Link>.
    </p>

    <h2>14. Changes to this Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. Material changes will be communicated
      by email or through the Platform at least 14 days before they take effect. The "Last
      updated" date at the top of the page reflects the most recent revision.
    </p>

    <h2>15. Contact</h2>
    <p>
      For any privacy question, or to exercise a right under GDPR:
    </p>
    <ul>
      <li>Email: <a href="mailto:privacy@studioscheduler.ie">privacy@studioscheduler.ie</a></li>
      <li>DPO: Chris Koble — <a href="mailto:dpo@studioscheduler.ie">dpo@studioscheduler.ie</a></li>
      <li>Post: Data Protection, Borrow A Geek Limited, 18 Henry Street, Kilrush, County Clare V15 CX71, Republic of Ireland</li>
    </ul>
  </PolicyLayout>
);

export default PrivacyPolicyPage;
